import React from "react";
import { GoogleOutlined, AppleOutlined } from "@ant-design/icons";
import LogoMark from "../../icons/newicons/topSIdebarLogo.png";
import qrAndroid from "../../assets/app-promo/qr-android.png";
import qrIos from "../../assets/app-promo/qr-ios.png";
import screenDashboard from "../../assets/app-promo/screen-dashboard.png";
import screenLookup from "../../assets/app-promo/screen-lookup.png";
import screenSearchResults from "../../assets/app-promo/screen-search-results.png";
import screenInventory from "../../assets/app-promo/screen-inventory.png";
import "./app-download-modal.css";

/**
 * Default store URLs (AB CatTech). Override per environment with:
 * REACT_APP_AB_CATTECH_PLAY_STORE_URL / REACT_APP_AB_CATTECH_APP_STORE_URL
 */
const DEFAULT_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.catphone&hl=en_IN";
const DEFAULT_APP_STORE_URL =
  "https://apps.apple.com/us/app/ab-cattech/id6763750382";

const PLAY_STORE_URL =
  process.env.REACT_APP_AB_CATTECH_PLAY_STORE_URL || DEFAULT_PLAY_STORE_URL;
const APP_STORE_URL =
  process.env.REACT_APP_AB_CATTECH_APP_STORE_URL || DEFAULT_APP_STORE_URL;

const FEATURES = [
  {
    title: "Instant OEM lookup",
    body: "Search converter references from the field.",
  },
  {
    title: "Camera-based grading",
    body: "Capture photos for team review or pricing context.",
  },
  {
    title: "Track your items",
    body: "Keep inventory and workflow status visible.",
  },
  {
    title: "Price alerts & market updates",
    body: "Stay updated on market movements.",
  },
  {
    title: "Secure recycling workflow",
    body: "Connect mobile work to compliance-driven processes.",
  },
];

function StoreLink({ href, icon, label, disabledTitle }) {
  const ready = Boolean(href);
  return (
    <a
      className={`app-download-modal__store-link${
        ready ? "" : " app-download-modal__store-link--disabled"
      }`}
      href={ready ? href : "#"}
      target={ready ? "_blank" : undefined}
      rel={ready ? "noopener noreferrer" : undefined}
      title={ready ? undefined : disabledTitle}
      aria-disabled={!ready}
      onClick={(e) => {
        if (!ready) e.preventDefault();
      }}
    >
      {icon}
      <span>{label}</span>
    </a>
  );
}

const AppDownloadModalContent = () => {
  return (
    <div className="app-download-modal">
      <div className="app-download-modal__grid">
        <section className="app-download-modal__left" aria-labelledby="app-dl-headline">
          <p className="app-download-modal__eyebrow">Mobile app now available</p>
          <h2 id="app-dl-headline" className="app-download-modal__headline">
            Run converter lookups, inventory, and pricing from your phone.
          </h2>
          <p className="app-download-modal__lede">
            Download the AB CatTech app to search OEM numbers, upload converter photos,
            track inventory, monitor market updates, and manage recycling workflows
            wherever your team is working.
          </p>
          <div className="app-download-modal__stores">
            <StoreLink
              href={PLAY_STORE_URL}
              icon={<GoogleOutlined style={{ fontSize: 16 }} />}
              label="Get it on Google Play"
              disabledTitle="Play Store link not configured yet"
            />
            <StoreLink
              href={APP_STORE_URL}
              icon={<AppleOutlined style={{ fontSize: 16 }} />}
              label="Download on the App Store"
              disabledTitle="App Store link not configured yet"
            />
          </div>
          <ul className="app-download-modal__features">
            {FEATURES.map((f) => (
              <li key={f.title} className="app-download-modal__feature">
                <span className="app-download-modal__feature-icon" aria-hidden />
                <div>
                  <span className="app-download-modal__feature-title">{f.title}</span>
                  {f.body}
                </div>
              </li>
            ))}
          </ul>
          <p className="app-download-modal__footnote">
            Built by A&amp;B Converters, Inc. for faster catalytic converter recycling
            workflows.
          </p>
        </section>

        <section className="app-download-modal__right" aria-label="App preview">
          <div className="app-download-modal__right-top">
            <img
              src={LogoMark}
              alt="AB CatTech"
              className="app-download-modal__logo"
            />
          </div>
          <div
            className="app-download-modal__mockups"
            aria-label="AB CatTech app screenshots"
          >
            <div className="app-download-modal__phone app-download-modal__phone--tilt-left">
              <img src={screenLookup} alt="Lookup: search by OEM" />
            </div>
            <div className="app-download-modal__phone app-download-modal__phone--center">
              <img src={screenDashboard} alt="Home dashboard" />
            </div>
            <div className="app-download-modal__phone app-download-modal__phone--tilt-right">
              <img src={screenSearchResults} alt="Lookup results" />
            </div>
            <div className="app-download-modal__phone app-download-modal__phone--rear">
              <img src={screenInventory} alt="Inventory" />
            </div>
          </div>
          <p className="app-download-modal__right-tag">Application</p>
          <h3 className="app-download-modal__right-headline">
            Built for converter work in motion.
          </h3>
          <div className="app-download-modal__qr-row">
            <div className="app-download-modal__qr-card app-download-modal__qr-card--android">
              <div className="app-download-modal__qr-title">Android</div>
              <a
                className="app-download-modal__qr-img-wrap"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={qrAndroid}
                  alt="Scan to open AB CatTech on Google Play"
                  className="app-download-modal__qr-img"
                />
              </a>
              <p className="app-download-modal__qr-copy">
                Scan or tap the code, or use{" "}
                <strong>Get it on Google Play</strong> on the left.
              </p>
            </div>
            <div className="app-download-modal__qr-card app-download-modal__qr-card--ios">
              <div className="app-download-modal__qr-title">iOS</div>
              <a
                className="app-download-modal__qr-img-wrap app-download-modal__qr-img-wrap--ios"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={qrIos}
                  alt="Scan to open AB CatTech on the App Store"
                  className="app-download-modal__qr-img"
                />
              </a>
              <p className="app-download-modal__qr-copy">
                Scan or tap the code, or use{" "}
                <strong>Download on the App Store</strong> on the left.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AppDownloadModalContent;
